<template>
  <div class="flex flex-col gap-0.5">
    <BaseSwitchListItem
      v-model="isRecurring"
      class="bg-surface-container rounded-large"
      :class="[isRecurring && 'rounded-b-small']"
      :title="$t('survey.enableRecurrence')"
      :supporting-text="$t('survey.enableRecurrenceDescription')"
      icon="repeat"
    />
    <template v-if="isRecurring">
      <SurveyStartDateForm
        v-if="showStartDate"
        v-model="startDate"
        :label="$t('survey.nextOccurrenceAt')"
      />
      <div
        class="bg-surface-container rounded-b-large rounded-t-small flex flex-col gap-3 p-4 pl-14"
      >
        <div class="flex flex-row items-center gap-1">
          <label for="amount" class="text-title-medium grow">{{ $t('survey.repeat') }}</label>
          <input
            id="amount"
            v-model.number="recurrenceInterval"
            type="number"
            min="1"
            class="border-outline-variant rounded-small ml-2 h-8 w-16 border px-2 text-center"
          />
          <BaseMenu
            :items="
              Object.values(SurveyRecurrenceFrequency).map((frequency) => ({
                id: frequency,
                title: $t('survey.recurrenceFrequency.' + frequency, {
                  count: recurrenceInterval,
                }),
                leadingIcon: recurrenceFrequency === frequency ? 'check' : undefined,
                callback: () => {
                  recurrenceFrequency = frequency
                },
              }))
            "
          >
            <template #default="{ open }">
              <BaseChip type="filter" :menu="open ? 'open' : 'close'">{{
                $t('survey.recurrenceFrequency.' + recurrenceFrequency, {
                  count: recurrenceInterval,
                })
              }}</BaseChip>
            </template>
          </BaseMenu>
        </div>
        <p class="text-body-medium text-on-surface-variant">
          {{ recurrenceSummaryText }}
        </p>
      </div>
      <p v-if="props.recurrenceIntervalError" class="text-error text-body-small mt-1 px-1">
        {{ props.recurrenceIntervalError }}
      </p>
    </template>
  </div>
</template>

<script setup lang="ts">
  import { computed } from 'vue'
  import BaseSwitchListItem from '@/core/components/base/switch-list-item/BaseSwitchListItem.vue'
  import BaseMenu from '@/core/components/base/menu/BaseMenu.vue'
  import BaseChip from '@/core/components/base/chip/BaseChip.vue'
  import { SurveyRecurrenceFrequency } from '../composables/use-start-survey'
  import { useSurveyRecurrenceSummary } from '../composables/use-survey-recurrence-summary'
  import SurveyStartDateForm from './SurveyStartDateForm.vue'

  interface Props {
    membersError?: string
    recurrenceIntervalError?: string
    showStartDate?: boolean
  }

  const props = withDefaults(defineProps<Props>(), {
    membersError: undefined,
    recurrenceIntervalError: undefined,
    showMembers: true,
    showRecurrence: true,
    autofocusMembers: false,
    memberFilter: 'coachees',
    showEnableToggle: true,
  })

  const isRecurring = defineModel<boolean>('isRecurring', { required: true })
  const recurrenceInterval = defineModel<number | undefined>('recurrenceInterval', {
    required: true,
  })

  const startDate = defineModel<Date | undefined>('startDate', {
    required: true,
  })

  const recurrenceFrequency = defineModel<SurveyRecurrenceFrequency | undefined>(
    'recurrenceFrequency',
    { required: true },
  )

  const { recurrenceSummaryText } = useSurveyRecurrenceSummary(
    computed(() => new Date(startDate.value ?? new Date())),
    recurrenceInterval,
    recurrenceFrequency,
  )
</script>
